import { supabase } from '../backend/supabase';
import { defaultConfig } from '../utils/config';

export type REQUEST_TYPE = 'generate-animation' | 'remove-video-background';

interface BaseAnimateRequest {
    request_type: REQUEST_TYPE;
    animation_id: string;
    animation_history_id?: string;
}

interface GenerateAnimationRequest extends BaseAnimateRequest {
    request_type: 'generate-animation';
    reference_image: string; // url
    user_prompt: string;
    transparent_background?: boolean;
    loop?: boolean;
    enable_prompt_enhance?: boolean;
}

interface RemoveBackgroundRequest extends BaseAnimateRequest {
    request_type: 'remove-video-background';
    reference_video: string; // url
}

export type AnimateRequest = GenerateAnimationRequest | RemoveBackgroundRequest;

interface AnimateResult {
	video?: string; // url
	images?: string[]; // array of urls
	duration?: number;
}

export async function animate(
  req: AnimateRequest, 
): Promise<AnimateResult> {
  const { request_type, animation_id, animation_history_id } = req;
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
      throw new Error('User must be authenticated to perform this action');
    }

    // Determine target URL based on config
    let targetUrl: string;
    
    if (defaultConfig.USE_SUPABASE_ANIMATE) {
      // Use the Supabase Edge Function endpoint
      targetUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/animate`;
    } else {
      // Use the Cloudflare Worker endpoint
      const workerUrl = process.env.REACT_APP_CLOUDFLARE_ANIMATE_URL;
      if (!workerUrl) {
        throw new Error('REACT_APP_CLOUDFLARE_ANIMATE_URL environment variable is not set');
      }
      targetUrl = workerUrl;
    }

    let requestBody: Record<string, any> = {
      request_type,
      animation_id,
      animation_history_id,
    };

    // Type discrimination logic
    if (request_type === 'generate-animation') {
      const generateReq = req as GenerateAnimationRequest;
      requestBody = {
        ...requestBody,
        reference_image: generateReq.reference_image,
        user_prompt: generateReq.user_prompt,
        transparent_background: generateReq.transparent_background,
        loop: generateReq.loop,
        enable_prompt_enhance: generateReq.enable_prompt_enhance,
      };
    } else if (request_type === 'remove-video-background') {
      const removeBgReq = req as RemoveBackgroundRequest;
      requestBody = {
        ...requestBody,
        reference_video: removeBgReq.reference_video,
      };
    }

    // console.log(`Sending request to ${defaultConfig.USE_SUPABASE_ANIMATE ? 'Supabase' : 'Cloudflare'}:`, requestBody);

    const response = await fetch(targetUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const responseText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        errorData = { error: responseText || 'Unknown error' };
      }
      throw new Error(`Animation service error: ${errorData.error || response.statusText}`);
    }

    const result: AnimateResult = await response.json();
    return result;
  } catch (error) {
    console.error('Error during animation generation:', error);
    throw error;
  }
}